// SkipInventoryDetailPage — /skip/inventory/:id.
//
// Shows unit details + the full job history for this unit, fetched via
// useSkipJobs({ skip_inventory_uuid }) (per §7.10 server filter).

import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { useAuthStore } from '../../../store/authStore';
import {
  useDeleteSkipInventoryItem,
  useInventoryHistory,
  useSkipInventoryItem,
} from '../hooks/useSkipInventory';
import { useSkipJobs } from '../hooks/useSkipJobs';
import { SkipInventoryStatusBadge } from './components/SkipInventoryStatusBadge';
import { SkipDaysOutBadge } from './components/SkipDaysOutBadge';
import { SkipJobStatusBadge } from '../jobs/components/SkipJobStatusBadge';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './SkipInventoryDetailPage.module.css';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function SkipInventoryDetailPage(): JSX.Element {
  const { id: encId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const itemQuery = useSkipInventoryItem(encId);
  const jobs = useSkipJobs({ skip_inventory_uuid: itemQuery.data?.item.uuid, limit: 50 });
  const deleteMutation = useDeleteSkipInventoryItem(encId);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (itemQuery.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading…" />
      </div>
    );
  }

  if (itemQuery.isError || !itemQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Inventory unit not found" />
        <Card>
          <p>This unit could not be loaded.</p>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/skip/inventory')}>
            Back to Inventory
          </Button>
        </Card>
      </div>
    );
  }

  const item = itemQuery.data.item;

  function handleDelete() {
    setDeleteError(null);
    deleteMutation.mutate(undefined, {
      onSuccess: () => navigate('/skip/inventory'),
      onError: (err) => setDeleteError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={item.skip_number}
        subtitle={`${item.stock_type_name ?? '—'} — ${item.size_name ?? '—'}`}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate('/skip/inventory')}
            >
              Back
            </Button>
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() => navigate(`/skip/inventory/${encodeURIComponent(encId)}/edit`)}
            >
              Edit
            </Button>
            {isAdmin ? (
              <Button
                variant="danger"
                icon={Trash2}
                onClick={() => setConfirmDelete(true)}
                disabled={deleteMutation.isPending}
              >
                Delete
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <header className={styles.sectionHeader}>
          <h2>Unit Details</h2>
          <div className={styles.badges}>
            <SkipInventoryStatusBadge status={item.effective_status} />
            <SkipDaysOutBadge activeJobDeliveredAt={item.active_job_delivered_at} />
          </div>
        </header>
        <dl className={styles.dl}>
          <dt>Unit Number</dt>
          <dd className={styles.mono}>{item.skip_number}</dd>
          <dt>Stock Type</dt>
          <dd>{item.stock_type_name ?? '—'}</dd>
          <dt>Size</dt>
          <dd>{item.size_name ?? '—'}</dd>
          <dt>Condition</dt>
          <dd>{item.skip_condition.replaceAll('_', ' ')}</dd>
          <dt>Schema Status</dt>
          <dd>{item.status}</dd>
          <dt>Current Location</dt>
          <dd>{item.current_location ?? '—'}</dd>
          <dt>Last Service</dt>
          <dd>{fmtDate(item.last_service_date)}</dd>
          <dt>Next Service</dt>
          <dd>{fmtDate(item.next_service_date)}</dd>
          <dt>Notes</dt>
          <dd>{item.notes ?? '—'}</dd>
        </dl>
      </Card>

      {item.active_job_uuid ? (
        <Card>
          <header className={styles.sectionHeader}>
            <h2>Current Job</h2>
          </header>
          <p>
            <Link
              to={`/skip/jobs/${encodeURIComponent(item.active_job_uuid)}`}
              className={styles.cellLink}
            >
              {item.active_job_number ?? 'View job →'}
            </Link>
            {item.active_job_delivered_at ? (
              <span className={styles.muted}>
                {' '}
                · delivered {fmtDate(item.active_job_delivered_at)}
              </span>
            ) : null}
          </p>
        </Card>
      ) : null}

      <Card>
        <header className={styles.sectionHeader}>
          <h2>Jobs</h2>
        </header>
        {jobs.isLoading ? (
          <p className={styles.empty}>Loading jobs…</p>
        ) : (jobs.data?.jobs.length ?? 0) === 0 ? (
          <p className={styles.empty}>No jobs recorded for this unit yet.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Job #</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Delivery</th>
                <th>Collection</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {jobs.data?.jobs.map((j) => (
                <tr key={j.enc_id}>
                  <td className={styles.mono}>
                    <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`}>{j.job_number}</Link>
                  </td>
                  <td>{j.customer_name}</td>
                  <td>{j.job_type.replaceAll('_', ' ')}</td>
                  <td>{fmtDate(j.delivery_date)}</td>
                  <td>{fmtDate(j.collection_date)}</td>
                  <td>
                    <SkipJobStatusBadge status={j.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <InventoryHistoryCard inventoryEncId={encId} />

      {confirmDelete ? (
        <Card>
          <h2 className={styles.sectionHeader}>Delete this unit?</h2>
          <p className={styles.deleteHint}>
            Deleting <strong>{item.skip_number}</strong> is permanent. The unit will be
            removed from the fleet listing and cannot be re-used for new jobs.
          </p>
          {deleteError ? <p className={styles.error}>{deleteError}</p> : null}
          <div className={styles.deleteActions}>
            <Button
              variant="secondary"
              onClick={() => setConfirmDelete(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete Unit'}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

// ============================================================ HISTORY CARD (Phase 6H)

const HISTORY_EVENT_LABELS: Readonly<Record<string, string>> = {
  assigned: 'Assigned to job',
  marked_out: 'Marked out (delivered)',
  marked_available: 'Returned to yard',
  freed: 'Freed (exchange)',
  child_assigned: 'Assigned to child job',
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function InventoryHistoryCard({ inventoryEncId }: { inventoryEncId: string }): JSX.Element {
  const historyQuery = useInventoryHistory(inventoryEncId);
  const rows = historyQuery.data?.history ?? [];
  return (
    <Card>
      <header className={styles.sectionHeader}>
        <h2>Job History</h2>
      </header>
      {historyQuery.isLoading ? (
        <p className={styles.empty}>Loading history…</p>
      ) : rows.length === 0 ? (
        <p className={styles.empty}>No job history for this unit.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>Event</th>
              <th>Job Number</th>
              <th>Staff</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row.created_at}-${idx}`}>
                <td>{fmtDateTime(row.created_at)}</td>
                <td>{HISTORY_EVENT_LABELS[row.event] ?? row.event}</td>
                <td className={styles.mono}>
                  <Link to={`/skip/jobs/${encodeURIComponent(row.job_enc_id)}`}>
                    {row.job_number}
                  </Link>
                </td>
                <td>{row.actor_name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
